export interface RetryOptions {
  maxRetries: number
  delayMs?: number
  backoffMultiplier?: number
  onRetry?: (attempt: number, error: any) => void
  onFinalFailure?: (error: any, attempts: number) => void
}

export interface RetryResult<T> {
  success: boolean
  result?: T
  error?: any
  attempts: number
}

export async function withRetry<T>(
  operation: (attempt: number) => Promise<T>,
  options: RetryOptions
): Promise<RetryResult<T>> {
  const {
    maxRetries,
    delayMs = 1000,
    backoffMultiplier = 1,
    onRetry,
    onFinalFailure,
  } = options

  let lastError: any

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await operation(attempt)
      return { success: true, result, attempts: attempt }
    } catch (error) {
      lastError = error

      if (attempt < maxRetries) {
        onRetry?.(attempt, error)

        if (delayMs > 0) {
          const delay = delayMs * Math.pow(backoffMultiplier, attempt - 1)
          await new Promise((resolve) => setTimeout(resolve, delay))
        }
      }
    }
  }

  onFinalFailure?.(lastError, maxRetries)
  return { success: false, error: lastError, attempts: maxRetries }
}

export async function retryOAuthOperation<T>(
  operation: (attempt: number) => Promise<T>,
  maxRetries: number = 3,
  delayMs: number = 2000
): Promise<RetryResult<T>> {
  return withRetry(operation, {
    maxRetries,
    delayMs,
    onRetry: () => {},
    onFinalFailure: () => {},
  })
}

export async function retryApiCall<T>(
  apiCall: (token: string) => Promise<T>,
  getToken: () => Promise<string>,
  refreshToken: () => Promise<string>,
  maxRetries: number = 3
): Promise<RetryResult<T>> {
  let currentToken = await getToken()

  return withRetry(
    async (attempt) => {
      try {
        return await apiCall(currentToken)
      } catch (error: any) {
        if (error?.status === 401 && attempt < maxRetries) {
          currentToken = await refreshToken()
          return await apiCall(currentToken)
        }
        throw error
      }
    },
    {
      maxRetries,
      delayMs: 1000,
      onRetry: () => {},
      onFinalFailure: () => {},
    }
  )
}
